<template>
  <div class="mb-8">
    <v-row class="my-0">
      <v-col cols="auto" class="py-0">
        <HeadingBox icon="mdi-timer-outline" title="Send behandlingstid til patient manuelt" :loading="appointmentsLoading"/>
      </v-col>
      <v-col cols="auto" class="py-0 grow pt-1">
        <div v-if="isEmailType && patientEmail" class="d-flex justify-lg-end align-center text-body-2 mb-4 mb-lg-0">
          <v-chip label small class="mr-1">Patient email</v-chip>
          {{ patientEmail }}
        </div>
      </v-col>
    </v-row>
    <v-row class="my-0">
      <v-col cols="12" lg="6" class="py-0 pl-3 pl-md-16 pl-lg-16 mb-4 mb-lg-0">
        <template v-if="appointmentsList.length > 0">
          <div v-for="appointmentItem in appointmentsList" class="mb-2 d-flex flex-wrap align-center">
            <v-tooltip bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="20" class="mr-2">{{ typeIcon(appointmentItem['appointmentType']) }}</v-icon>
              </template>
              <span>{{ typeText(appointmentItem['appointmentType']) }}</span>
            </v-tooltip>
            <v-tooltip bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="20" :class="['mr-4', logStatusIconColor(appointmentItem['status'])]">{{ logStatusIconName(appointmentItem['status']) }}</v-icon>
              </template>
              <span>{{ logStatusText(appointmentItem['status']) }}</span>
            </v-tooltip>
            <span class="text-body-1 mr-4 text-no-wrap">{{ appointmentItem['sendDate'] ? formattedDate(appointmentItem['sendDate']) : formattedDate(appointmentItem['createDate']) }}</span>
            <span v-if="!appointmentItem['sendDate']" class="text-body-1 mr-4 text-no-wrap">Planlagt til afsendelse</span>
            <v-tooltip v-if="appointmentItem['content']" bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click="openAppointmentDialog(appointmentItem['content'])">
                  <v-icon size="20">mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Forhåndsvisning</span>
            </v-tooltip>
            <EboksAttachment
              v-for="attachment in (appointmentItem['eboksMessageAttachments'] || [])"
              :key="`appointment-${appointmentItem['createDate']}-${attachment['id']}`"
              :visit-id="appointmentItem['visitId'] != null ? appointmentItem['visitId'] : visitItem.id"
              :attachment-id="attachment['id']"
              :attachment-type="attachment['type']"
            />
          </div>
        </template>
        <span v-if="appointmentsList.length === 0" class="d-block text-body-1 grey--text">Ingen data...</span>
      </v-col>
      <v-col cols="12" lg="6" class="py-0 pl-3 pl-md-16 pl-lg-3">
        <div class="d-lg-flex justify-lg-end align-center">
          <div v-if="isEmailType && !patientEmail" class="d-flex justify-end align-center text-body-2 red--text">
            <v-icon size="20" class="mr-1 red--text">mdi-alert-circle</v-icon>
            Patienten har ingen e-mail
          </div>
        </div>
        <div class="d-lg-flex justify-lg-end align-center">
          <v-radio-group hide-details row v-model="appointmentType" class="my-0 pt-0 mb-4 mb-lg-0">
            <v-radio class="my-2 mr-10" label="e-Boks" :value="2" color="primary" :disabled='patientDoesntHaveEboks'/>
            <v-radio class="my-2 mr-10" label="Email" :value="0" color="primary" :disabled="!patientEmail"/>
          </v-radio-group>
          <v-btn depressed :color="appointmentSendButtonColor" :loading="appointmentSendLoading" :disabled="appointmentSendLoading || appointmentsLoading || (appointmentType === null) || (isEmailType && !patientEmail)" @click="sendAppointment">Send tid</v-btn>
        </div>
      </v-col>
    </v-row>
    <v-dialog v-model="appointmentDialog" width="700" overlay-color="#fff" :overlay-opacity="0.7" @click:outside="closeAppointmentDialog">
      <v-card>
        <v-card-text class="py-4">
          <div v-html="appointmentDialogContent" class="pa-3 rounded" style="background-color: rgba(0, 0, 0, 0.05);"/>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="black darken-1" text @click="appointmentDialog = false; closeAppointmentDialog()">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import HeadingBox from '../common/HeadingBox';
  import EboksAttachment from '@/components/common/EboksAttachment.vue';
  import methods from '../../utils/methods';
  import api from '../../api/api';
  import logStatusIconMixin from '@/mixins/logStatusIconMixin';

  export default {
    name: 'OverviewAppointment',
    components: {HeadingBox, EboksAttachment},
    mixins: [logStatusIconMixin],

    props: {
      visitItem: {
        type: Object,
      },
      patientEmail: {
        type: String
      },
      patientDoesntHaveEboks: {
        type: Boolean
      }
    },

    data: function () {
      return {
        appointmentDialog: false,
        appointmentDialogContent: null,
        appointmentsLoading: false,
        appointmentsList: [],
        appointmentType: null,
        appointmentSendLoading: false,
        appointmentSendButtonColor: 'primary',
      };
    },

    mounted: function () {
      this.setAppointmentType();
      this.getAppointments();
    },

    watch: {
      patientDoesntHaveEboks(newValue, oldValue) {
        this.setAppointmentType();
      },
    },

    computed: {
      isEmailType() {
        return this.appointmentType === 0;
      },
    },

    methods: {
      openAppointmentDialog: function (content) {
        this.appointmentDialogContent = content;
        this.appointmentDialog = true;
      },
      closeAppointmentDialog: function () {
        setTimeout(() => {
          this.appointmentDialogContent = null;
        }, 100);
      },
      formattedDate: function (value) {
        if (value) {
          return`${methods.getDateFromIso(value)}, ${methods.getTimeFromIso(value)}`;
        }
        return '';
      },
      getAppointments: async function () {
        this.appointmentsLoading = true;
        let newAppointments = await api.getAppointments(this.visitItem.id);
        this.appointmentsLoading = false;
        this.appointmentsList = newAppointments.data;
      },
      sendAppointment: async function () {
        try {
          this.appointmentSendButtonColor = 'primary';
          this.appointmentSendLoading = true;
          let newAppointments = await api.sendAppointment(this.visitItem.id, this.appointmentType);
          this.appointmentsList = newAppointments.data;
          this.appointmentSendLoading = false;
        } catch (e) {
          if (e.response.status === 403) {
            methods.showGlobalSnackbar('error', 'Der eksisterer allerede en patient med denne mail - blot med andet cpr. nummer. Patientens mail skal være unik.');
          }
          if (e.response.status === 422) {
            methods.showGlobalSnackbar('error', 'Udfyld venligst E-mail-feltet.');
          }
          this.appointmentSendLoading = false;
          this.appointmentSendButtonColor = 'error';
        }
      },
      setAppointmentType: function () {
        if (this.patientDoesntHaveEboks && this.patientEmail) {
          this.appointmentType = 0;
          return;
        }
        if (!this.patientDoesntHaveEboks) {
          this.appointmentType = 2;
          return
        }
        this.appointmentType = null;
      },
      typeIcon: function (type) {
        let names = {
          0: 'mdi-at',
          1: 'mdi-email',
          2: 'mdi-alpha-e-box'
        };
        return names[type];
      },
      typeText: function (type) {
        let text = {
          0: 'Email',
          1: 'Brev',
          2: 'e-Boks'
        };
        return text[type];
      },
    },
  };
</script>
